import type { DropdownState, FocusedElement } from '../state/types';
import { ARIA_LABELS } from '../types';
import type { OptionCallPut } from '../types';
import { ClearButton } from './ClearButton';
import {
    SymbolPickerMenu,
    SymbolPickerMenuTrigger,
    SymbolPickerMenuContent,
    SymbolPickerMenuList,
    SymbolPickerMenuItem,
} from './SymbolPickerMenu';
import { cn } from '@thinkalpha/ui-core/utils';
import { useRef } from 'react';
import { useEvent } from 'src/hooks/useEvent';

export interface OptionsCallPutPickerProps {
    value?: OptionCallPut;
    onChange: (type: OptionCallPut | undefined) => void;
    className?: string;
    placeholder?: string;
    dropdownOpen: boolean;
    dropdown: DropdownState;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
}

export const OptionsCallPutPicker = ({
    value,
    onChange,
    className,
    placeholder,
    dropdownOpen,
    dropdown,
    onDropdownActiveIndex,
    onFocus,
}: OptionsCallPutPickerProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    // Ref for the dialog element
    const dialogRef = useRef<HTMLDivElement>(null);

    // Set up dropdown items when focused
    const handleFocus = useEvent((e: React.FocusEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFocus('optionCallPut');
    });

    // Create specific handlers for this element using useEvent
    const handleDropdownActiveIndex = useEvent((index: number) => {
        onDropdownActiveIndex(index);
    });

    const handleInputClick = useEvent((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFocus('optionCallPut');
    });

    // blur is allowed to propagate to the parent container
    // unless the blur event comes from focus shifting to the dropdown
    const handleBlur = useEvent((e: React.FocusEvent) => {
        const next = e.relatedTarget;
        if (next && dialogRef.current?.contains(next)) {
            e.stopPropagation();
        }
    });

    const handleClear = useEvent(() => {
        onChange(undefined);
    });

    const handleItemClick = useEvent((type: OptionCallPut, _index: number) => {
        onChange(type);
    });

    const handleItemMouseEnter = useEvent((index: number) => {
        handleDropdownActiveIndex(index);
    });

    return (
        <SymbolPickerMenu open={dropdownOpen}>
            <SymbolPickerMenuTrigger>
                <button
                    ref={buttonRef}
                    type="button"
                    tabIndex={-1}
                    aria-label={ARIA_LABELS.optionCallPut}
                    className={cn(
                        'h-4 rounded-md p-0 px-1 text-xs whitespace-nowrap focus:border-gray-500 focus:ring-1 focus:outline-none',
                        value === 'C' || value === 'P' ? 'text-white' : 'text-gray-250',
                        className,
                    )}
                    onClick={handleInputClick}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                >
                    {value === 'C' ? 'CALL' : value === 'P' ? 'PUT' : (placeholder ?? 'C/P')}
                </button>
            </SymbolPickerMenuTrigger>
            <SymbolPickerMenuContent className="w-24 p-0" ref={dialogRef}>
                <SymbolPickerMenuList className="group">
                    {dropdown.filteredItems.map((type: OptionCallPut, index: number) => (
                        <div key={type} className="flex items-center">
                            <SymbolPickerMenuItem
                                selected={dropdown.activeIndex === index}
                                onSelect={() => handleItemClick(type, index)}
                                onMouseEnter={() => handleItemMouseEnter(index)}
                                className="relative flex h-4 w-full flex-1 cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm outline-hidden select-none"
                            >
                                {type === 'C' ? 'CALL' : 'PUT'}
                            </SymbolPickerMenuItem>
                            {value === type && (
                                <ClearButton onClick={handleClear} className="z-10 mx-1 text-gray-200" />
                            )}
                        </div>
                    ))}
                </SymbolPickerMenuList>
            </SymbolPickerMenuContent>
        </SymbolPickerMenu>
    );
};
